<template>
  <div
    class="select-none relative py-3"
    style="height: 120px; cursor: crosshair"
    ref="container"
    @mousedown="onMouseDown"
    @mousemove="onHover"
    @wheel.prevent="onWheel"
  >
    <!-- Clipped track: content bar, cuts, selection -->
    <div class="absolute overflow-hidden" style="left: 40px; right: 40px; top: 12px; bottom: 12px" ref="track">

      <!-- Unavailable zone (before first segment) -->
      <div v-if="unavailableBarStyle" class="absolute h-12 rounded-l" style="top: 22px; background: repeating-linear-gradient(45deg, #f3f4f6, #f3f4f6 4px, #e5e7eb 4px, #e5e7eb 8px)" :style="unavailableBarStyle" />

      <!-- Content bar (available zone) -->
      <div class="absolute h-12 bg-gray-300 rounded" style="top: 22px" :style="contentBarStyle" />

      <!-- Commercial break regions — sit under cuts / selection so those
           always win the visual stack. Pointer events are enabled so
           right-click can open the selection popover; left-click still
           bubbles to the container's mousedown for normal seek. -->
      <div
        v-for="brk in visibleCommercialBreaks"
        :key="'brk-' + brk.id"
        class="absolute h-12 bg-amber-200/60 border border-amber-400/70 rounded hover:bg-amber-300/70 transition-colors"
        style="top: 22px"
        :style="breakRegionStyle(brk)"
        :title="`Ad break — ${formatBreakDuration(brk.duration)}\nRight-click for selection options`"
        @contextmenu.prevent.stop="onBreakContextMenu($event, brk.id)"
      />

      <!-- Cut overlays -->
      <div v-for="(cut, i) in cuts" :key="cut.id" class="absolute h-12 bg-green-100 border border-green-300 rounded" style="top: 22px" :style="cutStyle(cut)">
        <span class="absolute inset-0 flex items-center justify-center text-xs font-bold text-green-600">{{ i + 1 }}</span>
      </div>

      <!-- Commercial break flag markers — small "AD" pill above the content
           bar at each break's start; scan target when zoomed out. Also
           pointer-events-none so it never eats a seek click. -->
      <div
        v-for="brk in visibleCommercialBreaks"
        :key="'brk-flag-' + brk.id"
        class="absolute pointer-events-none"
        style="top: 6px"
        :style="{ left: timeToX(brk.startTime) + 'px' }"
      >
        <div class="flex items-center gap-1 -translate-x-1/2">
          <span class="px-1.5 py-0.5 rounded text-[10px] font-bold leading-none bg-amber-500 text-white shadow-sm">AD</span>
        </div>
        <div class="w-px h-3 bg-amber-500 mx-auto" />
      </div>

      <!-- Mark In only (no selection yet) -->
      <template v-if="markInTime !== null && !hasSelection">
        <div class="absolute pointer-events-none" style="top: 22px" :style="{ left: timeToX(markInTime) + 'px' }">
          <div class="w-0.5 h-12 -ml-px bg-blue-500 opacity-80" />
        </div>
        <div class="absolute pointer-events-none" style="top: 18px" :style="{ left: timeToX(markInTime) + 'px' }">
          <div class="w-3 h-3 -ml-1.5 rounded-full bg-blue-500 border-2 border-white shadow" />
        </div>
      </template>

      <!-- Selection area (both marks set) -->
      <template v-if="hasSelection">
        <div class="absolute h-12 bg-blue-500/10 border-2 border-blue-400/40 rounded-sm" style="top: 22px" :style="selectionStyle" />

        <div class="absolute pointer-events-none" style="top: 18px" :style="{ left: timeToX(markInTime) + 'px' }">
          <div class="w-3 h-3 -ml-1.5 rounded-full bg-blue-500 border-2 border-white shadow" />
        </div>

        <div class="absolute pointer-events-none" style="top: 18px" :style="{ left: timeToX(markOutTime) + 'px' }">
          <div class="w-3 h-3 -ml-1.5 rounded-full bg-blue-500 border-2 border-white shadow" />
        </div>
      </template>

    </div>

    <!-- Ticks + labels (outside clipped track so labels aren't clipped) -->
    <div class="absolute" style="left: 40px; right: 40px; top: 84px; height: 24px">
      <template v-for="tick in ticks" :key="tick.time">
        <div class="absolute top-0 w-px bg-gray-300" :style="{ left: timeToX(tick.time) + 'px', height: tick.major ? '8px' : '4px' }" />
        <span v-if="tick.major" class="absolute text-sm font-data text-gray-400 -translate-x-1/2 whitespace-nowrap" style="top: 10px" :style="{ left: timeToX(tick.time) + 'px' }">{{ formatTimeFull(tick.time) }}</span>
      </template>
    </div>

    <!-- Ad break selection popover — fixed-positioned at the right-click
         point, teleported to <body> so it can escape the timeline's own
         overflow-hidden bounds and paint over the player above. -->
    <Teleport to="body">
      <div
        v-if="contextMenu.open"
        ref="contextPopover"
        class="fixed z-[1000] bg-white rounded-lg shadow-xl border border-gray-200 py-1.5 min-w-[220px] animate-fade-in"
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      >
        <div class="px-3 py-2 border-b border-gray-100">
          <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Create selection</p>
        </div>
        <button
          @click="applyBreakSelection('this')"
          class="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-center justify-between text-sm text-gray-800"
        >
          <span class="flex items-center gap-2">
            <span class="w-1.5 h-1.5 rounded-full bg-amber-500" />
            This ad break
          </span>
          <span class="font-data text-xs text-gray-400">{{ contextThisBreak ? formatBreakDuration(contextThisBreak.duration) : '' }}</span>
        </button>
        <button
          @click="applyBreakSelection('before')"
          :disabled="!contextBefore.duration"
          class="w-full text-left px-3 py-2 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between text-sm text-gray-800"
        >
          <span class="flex items-center gap-2">
            <span class="w-1.5 h-1.5 rounded-full bg-gray-400" />
            Content before
          </span>
          <span class="font-data text-xs text-gray-400">{{ contextBefore.duration ? formatBreakDuration(contextBefore.duration) : '—' }}</span>
        </button>
        <button
          @click="applyBreakSelection('after')"
          :disabled="!contextAfter.duration"
          class="w-full text-left px-3 py-2 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between text-sm text-gray-800"
        >
          <span class="flex items-center gap-2">
            <span class="w-1.5 h-1.5 rounded-full bg-gray-400" />
            Content after
          </span>
          <span class="font-data text-xs text-gray-400">{{ contextAfter.duration ? formatBreakDuration(contextAfter.duration) : '—' }}</span>
        </button>
      </div>
    </Teleport>

    <!-- Playhead (outside clipped track so label is never cut off) -->
    <div
      v-if="playheadVisible"
      class="absolute z-30 pointer-events-none"
      style="top: 12px; bottom: 12px"
      :style="{ left: (40 + playheadX) + 'px' }"
    >
      <div class="playhead-indicator">
        <div :class="['playhead-label', playheadGray ? 'playhead-label-gray' : '']">{{ formatTimeFull(playheadTime) }}</div>
        <div :class="['playhead-triangle', playheadGray ? 'playhead-triangle-gray' : '']" />
        <div :class="['playhead-line', playheadGray ? 'playhead-line-gray' : '']" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'

export default {
  name: 'EditorTimeline',
  emits: ['seek', 'drag-start', 'drag-end'],

  computed: {
    ...mapState('editor', ['currentTime', 'duration', 'playing', 'mode', 'isLive', 'viewStart', 'viewDuration', 'markInTime', 'markOutTime', 'cuts', 'startDate', 'earliestAvailable', 'commercialBreaks']),
    ...mapGetters('editor', ['hasSelection', 'viewEnd']),

    visibleCommercialBreaks() {
      // Cheap culling: only render breaks that overlap the current view.
      // Long sources with many breaks would otherwise paint a lot of DOM
      // outside the visible strip.
      if (!this.commercialBreaks?.length) return []
      const vs = this.viewStart
      const ve = this.viewEnd
      return this.commercialBreaks.filter(b => b.endTime >= vs && b.startTime <= ve)
    },

    // Sorted view of every break — needed by the popover's "before"/"after"
    // math regardless of whether the break is in the current viewport.
    sortedCommercialBreaks() {
      if (!this.commercialBreaks?.length) return []
      return [...this.commercialBreaks].sort((a, b) => a.startTime - b.startTime)
    },

    contextThisBreak() {
      if (!this.contextMenu.open || !this.contextMenu.breakId) return null
      return this.commercialBreaks.find(b => b.id === this.contextMenu.breakId) || null
    },

    // Content between the previous break's end (or the source's earliest
    // playable point) and this break's start. Zero-length when the ad is
    // the first thing in the source or when two breaks touch.
    contextBefore() {
      const brk = this.contextThisBreak
      if (!brk) return { startTime: 0, endTime: 0, duration: 0 }
      const sorted = this.sortedCommercialBreaks
      const idx = sorted.findIndex(b => b.id === brk.id)
      const prev = idx > 0 ? sorted[idx - 1] : null
      const startTime = prev ? prev.endTime : (this.earliestAvailable || 0)
      const endTime = brk.startTime
      const duration = Math.max(0, endTime - startTime)
      return { startTime, endTime, duration }
    },

    // Content after the break, ending at the next break's start (or the
    // end of the source). Zero-length when the ad is the last thing we
    // know about (live edge, or last break in a recording).
    contextAfter() {
      const brk = this.contextThisBreak
      if (!brk) return { startTime: 0, endTime: 0, duration: 0 }
      const sorted = this.sortedCommercialBreaks
      const idx = sorted.findIndex(b => b.id === brk.id)
      const next = idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null
      const startTime = brk.endTime
      const endTime = next ? next.startTime : (this.duration || brk.endTime)
      const duration = Math.max(0, endTime - startTime)
      return { startTime, endTime, duration }
    },

    trackWidth() {
      return this.measuredWidth || 100
    },

    unavailableBarStyle() {
      if (!this.earliestAvailable || this.earliestAvailable <= 0) return null
      const left = this.timeToX(0)
      const right = this.timeToX(this.earliestAvailable)
      const width = right - left
      if (width <= 0) return null
      return { left: left + 'px', width: width + 'px' }
    },

    contentBarStyle() {
      const dur = this.duration || (this.mode === 'live' ? this.viewDuration : 0)
      if (!dur) return { left: '0px', width: '0px' }
      const start = this.earliestAvailable || 0
      const left = this.timeToX(start)
      const right = this.timeToX(dur)
      const width = Math.max(2, right - left)
      return { left: left + 'px', width: width + 'px' }
    },

    selectionStyle() {
      if (!this.hasSelection) return {}
      const left = this.timeToX(this.markInTime)
      const right = this.timeToX(this.markOutTime)
      return {
        left: left + 'px',
        width: Math.max(4, right - left) + 'px'
      }
    },

    playheadGray() {
      return this.mode === 'live' && !this.isLive
    },

    playheadTime() {
      // Always the real playback position. While live, `duration` is a smooth
      // wall-clock projection that runs ahead of playable content by the stream
      // latency — drawing the playhead there makes it lie about where the video
      // is, so a later seek appears to jump back by the whole latency.
      return this.currentTime
    },

    playheadX() {
      const x = this.timeToX(this.playheadTime)
      return Math.max(0, Math.min(this.trackWidth, x))
    },

    playheadVisible() {
      const x = this.timeToX(this.playheadTime)
      return x >= -10 && x <= this.trackWidth + 10
    },

    ticks() {
      if (!this.duration && this.mode !== 'live') return []
      const interval = this.getTickInterval()
      const minor = interval / 5
      const start = Math.max(0, Math.floor(this.viewStart / minor) * minor)
      const result = []
      for (let t = start; t <= this.viewEnd; t += minor) {
        const x = this.timeToX(t)
        if (x < -10 || x > this.trackWidth + 10) continue
        result.push({ time: t, major: Math.abs(t % interval) < 0.001 })
      }
      return result
    }
  },

  data() {
    return {
      isDragging: false,
      dragMode: null,
      dragStartX: 0,
      dragStartViewStart: 0,
      dragMoveOffset: 0,
      dragMoved: false,
      seekTimeOnClick: null,
      measuredWidth: 0,
      // Ad-break right-click popover state. `breakId` is the id from the
      // store's commercialBreaks so we don't hold a stale reference if the
      // list rebuilds under us.
      contextMenu: { open: false, x: 0, y: 0, breakId: null }
    }
  },

  watch: {
    // Live auto-follow: keep the playhead pinned by scrolling on the SAME
    // signal the playhead is drawn from (currentTime). Scrolling on `duration`
    // let the playhead drift — `duration` is a smooth edge projection and
    // `currentTime` is the media clock, and the two don't advance in perfect
    // lockstep, so the gap between them (the playhead's on-screen X) wandered.
    currentTime(ct) {
      if (this.mode === 'live' && this.isLive && this.playing && !this.isDragging) {
        this.SET_VIEW({ start: ct - this.viewDuration * 0.98 })
      }
    }
  },

  mounted() {
    this.updateWidth()
    this._resizeObserver = new ResizeObserver(() => this.updateWidth())
    if (this.$refs.track) this._resizeObserver.observe(this.$refs.track)
    // Global listeners for the popover — attached always, cheap no-ops
    // while closed. Cheaper than dynamically adding/removing on every open.
    window.addEventListener('mousedown', this.handleGlobalMousedown, true)
    window.addEventListener('keydown', this.handleGlobalKeydown)
  },

  beforeUnmount() {
    if (this._resizeObserver) this._resizeObserver.disconnect()
    window.removeEventListener('mousedown', this.handleGlobalMousedown, true)
    window.removeEventListener('keydown', this.handleGlobalKeydown)
  },

  methods: {
    ...mapMutations('editor', ['SET_VIEW', 'SET_MARK_IN', 'SET_MARK_OUT']),

    updateWidth() {
      if (this.$refs.track) {
        this.measuredWidth = this.$refs.track.clientWidth
      }
    },

    timeToX(t) {
      return ((t - this.viewStart) / this.viewDuration) * this.trackWidth
    },

    getTrackX(e) {
      const rect = this.$refs.track.getBoundingClientRect()
      return { x: e.clientX - rect.left, w: rect.width }
    },

    trackXToTime(x, w) {
      return this.viewStart + (x / w) * this.viewDuration
    },

    clampTime(t) {
      return Math.max(0, Math.min(this.duration || t, t))
    },

    cutStyle(cut) {
      const left = this.timeToX(cut.inTime)
      const right = this.timeToX(cut.outTime)
      return {
        left: left + 'px',
        width: Math.max(4, right - left) + 'px'
      }
    },

    breakRegionStyle(brk) {
      const left = this.timeToX(brk.startTime)
      const right = this.timeToX(brk.endTime)
      // Minimum 2px so a very short break stays visible when zoomed way out.
      return {
        left: left + 'px',
        width: Math.max(2, right - left) + 'px'
      }
    },

    formatBreakDuration(s) {
      if (!s || !isFinite(s)) return '0s'
      if (s < 60) return `${Math.round(s)}s`
      const m = Math.floor(s / 60)
      const sec = Math.round(s % 60)
      return sec ? `${m}m ${sec}s` : `${m}m`
    },

    onBreakContextMenu(e, breakId) {
      // Clamp so the popover never overflows the viewport. Assumed size
      // is a bit generous; the fixed min-width in the template is 220px
      // and content adds ~140px of height for the 3 rows + header.
      const menuWidth = 240
      const menuHeight = 160
      const x = Math.min(e.clientX, window.innerWidth  - menuWidth  - 8)
      const y = Math.min(e.clientY, window.innerHeight - menuHeight - 8)
      this.contextMenu = { open: true, x, y, breakId }
    },

    applyBreakSelection(mode) {
      const brk = this.contextThisBreak
      if (!brk) { this.contextMenu.open = false; return }
      let range
      if (mode === 'this')        range = { startTime: brk.startTime, endTime: brk.endTime }
      else if (mode === 'before') range = this.contextBefore
      else if (mode === 'after')  range = this.contextAfter
      if (!range || range.startTime >= range.endTime) {
        this.contextMenu.open = false
        return
      }
      // Order matters — SET_MARK_IN clears mark-out if the current mark-out
      // is before it. Set in first, then out; playhead follows.
      this.SET_MARK_IN(range.startTime)
      this.SET_MARK_OUT(range.endTime)
      this.$emit('seek', range.startTime)
      this.contextMenu.open = false
    },

    handleGlobalMousedown(e) {
      if (!this.contextMenu.open) return
      const popover = this.$refs.contextPopover
      // Ignore clicks that land INSIDE the popover — those are button
      // clicks the popover's own handlers deal with.
      if (popover && popover.contains(e.target)) return
      this.contextMenu.open = false
    },

    handleGlobalKeydown(e) {
      if (this.contextMenu.open && e.key === 'Escape') {
        this.contextMenu.open = false
      }
    },

    getTickInterval() {
      const vd = this.viewDuration
      if (vd <= 10) return 1
      if (vd <= 30) return 5
      if (vd <= 120) return 10
      if (vd <= 300) return 30
      if (vd <= 900) return 60
      if (vd <= 3600) return 300
      if (vd <= 7200) return 600
      if (vd <= 14400) return 1200
      if (vd <= 28800) return 1800
      return 3600
    },

    isInTicksZone(e) {
      const rect = this.$refs.container.getBoundingClientRect()
      const y = e.clientY - rect.top
      return y >= 84
    },

    formatTimeFull(s) {
      if (s == null || isNaN(s)) return '00:00:00'
      if (this.startDate) {
        const d = new Date(this.startDate.getTime() + s * 1000)
        const h = String(d.getHours()).padStart(2, '0')
        const m = String(d.getMinutes()).padStart(2, '0')
        const sec = String(d.getSeconds()).padStart(2, '0')
        return `${h}:${m}:${sec}`
      }
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = Math.floor(s % 60)
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    },

    // --- Mouse handlers ---

    startDrag(mode) {
      this.isDragging = true
      this.dragMode = mode
      this.$emit('drag-start')
      window.addEventListener('mousemove', this.onDragMove)
      window.addEventListener('mouseup', this.onDragEnd)
    },

    onMouseDown(e) {
      const { x, w } = this.getTrackX(e)
      const time = this.trackXToTime(x, w)

      if (this.hasSelection) {
        const inX = this.timeToX(this.markInTime)
        const outX = this.timeToX(this.markOutTime)

        if (Math.abs(x - inX) < 10) {
          this.startDrag('handle-start')
          return
        }
        if (Math.abs(x - outX) < 10) {
          this.startDrag('handle-end')
          return
        }
        if (x > inX + 10 && x < outX - 10) {
          this.dragMoveOffset = time - this.markInTime
          this.$refs.container.style.cursor = 'grabbing'
          this.startDrag('move')
          return
        }
      }

      if (e.shiftKey) {
        if (time < 0) return
        this.SET_MARK_IN(Math.max(0, time))
        this.SET_MARK_OUT(null)
        this.startDrag('select')
        return
      }

      if (e.button === 1) {
        this.dragStartX = e.clientX
        this.dragStartViewStart = this.viewStart
        this.dragMoved = false
        this.$refs.container.style.cursor = 'grabbing'
        this.startDrag('pan')
        return
      }

      if (e.button === 0 && this.isInTicksZone(e)) {
        this.dragStartX = e.clientX
        this.dragStartViewStart = this.viewStart
        this.dragMoved = false
        this.seekTimeOnClick = null
        this.$refs.container.style.cursor = 'grabbing'
        this.startDrag('pan')
        return
      }

      if (e.button === 0) {
        this.$emit('seek', this.clampTime(time))
        this.startDrag('seek')
      }
    },

    onHover(e) {
      if (this.isDragging) return

      if (this.isInTicksZone(e)) {
        this.$refs.container.style.cursor = 'grab'
        return
      }

      if (!this.hasSelection) {
        this.$refs.container.style.cursor = 'crosshair'
        return
      }

      const { x } = this.getTrackX(e)
      const inX = this.timeToX(this.markInTime)
      const outX = this.timeToX(this.markOutTime)

      if (Math.abs(x - inX) < 10 || Math.abs(x - outX) < 10) {
        this.$refs.container.style.cursor = 'ew-resize'
      } else if (x > inX + 10 && x < outX - 10) {
        this.$refs.container.style.cursor = 'grab'
      } else {
        this.$refs.container.style.cursor = 'crosshair'
      }
    },

    onDragMove(e) {
      if (!this.isDragging) return
      this.dragMoved = true

      const { x, w } = this.getTrackX(e)
      const time = this.trackXToTime(x, w)
      const clampedTime = Math.max(0, Math.min(time, this.duration || time))

      switch (this.dragMode) {
        case 'seek':
          this.$emit('seek', this.clampTime(time))
          break
        case 'select':
          this.SET_MARK_OUT(Math.max(clampedTime, this.markInTime + 0.1))
          break
        case 'handle-start':
          this.SET_MARK_IN(Math.min(clampedTime, this.markOutTime - 0.1))
          break
        case 'handle-end':
          this.SET_MARK_OUT(Math.max(clampedTime, this.markInTime + 0.1))
          break
        case 'move': {
          const selDuration = this.markOutTime - this.markInTime
          let newIn = clampedTime - this.dragMoveOffset
          const maxIn = this.duration ? this.duration - selDuration : newIn
          newIn = Math.max(0, Math.min(newIn, maxIn))
          this.SET_MARK_IN(newIn)
          this.SET_MARK_OUT(newIn + selDuration)
          break
        }
        case 'pan': {
          const dx = e.clientX - this.dragStartX
          const dt = (dx / w) * this.viewDuration
          const newStart = this.dragStartViewStart - dt
          this.SET_VIEW({ start: newStart })
          break
        }
      }
    },

    onDragEnd() {
      if (this.dragMode === 'pan' && !this.dragMoved && this.seekTimeOnClick != null) {
        this.$emit('seek', this.seekTimeOnClick)
      }
      this.isDragging = false
      this.dragMode = null
      this.dragMoved = false
      this.seekTimeOnClick = null
      this.$emit('drag-end')
      if (this.$refs.container) {
        this.$refs.container.style.cursor = 'crosshair'
      }
      window.removeEventListener('mousemove', this.onDragMove)
      window.removeEventListener('mouseup', this.onDragEnd)
    },

    onWheel(e) {
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        const delta = (e.deltaX || e.deltaY)
        const { w } = this.getTrackX(e)
        const dt = (delta / w) * this.viewDuration * 0.5
        const maxStart = Math.max(0, (this.duration || 0) - this.viewDuration)
        const newStart = Math.max(0, Math.min(maxStart, this.viewStart + dt))
        this.SET_VIEW({ start: newStart })
        return
      }

      const { x, w } = this.getTrackX(e)
      const ratio = x / w
      const timeAtCursor = this.viewStart + ratio * this.viewDuration

      const factor = e.deltaY > 0 ? 1.15 : 0.87
      const newDuration = Math.max(2, Math.min(this.duration || 3600, this.viewDuration * factor))
      const newStart = Math.max(0, timeAtCursor - ratio * newDuration)
      this.SET_VIEW({ start: newStart, duration: newDuration })
    }
  }
}
</script>

<style scoped>
.playhead-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
}
.playhead-label {
  padding: 2px 8px;
  border-radius: 6px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  color: #fff;
  background: #ef4444;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
.playhead-label-gray {
  background: #9ca3af;
}
.playhead-triangle {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #ef4444;
  flex-shrink: 0;
  margin-top: -1px;
}
.playhead-triangle-gray {
  border-top-color: #9ca3af;
}
.playhead-line {
  width: 2px;
  flex: 1;
  background: #ef4444;
  border-radius: 1px;
  margin-top: -2px;
}
.playhead-line-gray {
  background: #9ca3af;
}
</style>
